import { z } from "zod";
/**
 * Operator customizations layered on top of a device's DECLARED plot affordance.
 *
 * The declared plot (`device_affordance.params`: `{ window_s, series:[...] }`) is
 * overwritten on every device birth, so customizations live in a separate sparse
 * overlay (`device_plot_override.override`) and are merged here at read time. The
 * doc stores ONLY deltas — anything the operator didn't touch keeps inheriting
 * the declared value, so a firmware change to an untouched field flows straight
 * through. Reconciliation is best-effort: references to channels/axes that no
 * longer exist are dropped, never throwing. Mirrors the dashboard-layout resolve
 * pattern (store by stable key, filter stale against live entities, merge rest).
 */
/** Axis binding for a series: share the unit's axis, or get a dedicated one. */
export const PLOT_AXIS_BINDS = ["shared", "own"];
export const plotSeriesOverrideSchema = z.object({
    hidden: z.boolean().optional(),
    color: z.string().optional(),
    axis: z.enum(PLOT_AXIS_BINDS).optional(),
});
export const plotAddedSeriesSchema = z.object({
    key: z.string().min(1),
    color: z.string().optional(),
    axis: z.enum(PLOT_AXIS_BINDS).optional(),
});
export const plotAxisOverrideSchema = z.object({
    min: z.number().nullable().optional(),
    max: z.number().nullable().optional(),
    log: z.boolean().optional(),
});
export const plotOverrideSchema = z.object({
    /** Tweaks to declared series, keyed by channel key (hide / recolor / re-axis). */
    series: z.record(z.string(), plotSeriesOverrideSchema).optional(),
    /** Operator-added channels, in display order after the declared series. */
    addedSeries: z.array(plotAddedSeriesSchema).optional(),
    /** Axis limits / log scale, keyed by axis id (see {@link plotAxisId}). */
    axes: z.record(z.string(), plotAxisOverrideSchema).optional(),
});
/**
 * Stable identity for a y-axis. Series sharing an axis must produce the same id:
 * own-axis series each get their own (`own:<key>`); the rest group by unit
 * (`unit:<unit>`), or fall to a single default axis when unitless.
 */
export function plotAxisId(series) {
    if (series.own_axis)
        return `own:${series.key}`;
    if (series.unit)
        return `unit:${series.unit}`;
    return "_default";
}
/**
 * Merge a declared plot with its operator override into a render-ready plot.
 * Sparse and best-effort: hidden declared series are removed, added series are
 * filtered to channels that still exist, axis overrides are applied only to axes
 * still present, and untouched fields inherit the declaration.
 */
export function resolvePlot(declared, override, channels) {
    const channelByKey = new Map(channels.map((c) => [c.key, c]));
    const seriesOvr = override?.series ?? {};
    const axisOvr = override?.axes ?? {};
    const out = [];
    const declaredKeys = new Set();
    // 1. Declared series: drop hidden + vanished channels; apply color/axis tweaks.
    for (const d of declared.series ?? []) {
        declaredKeys.add(d.key);
        if (!channelByKey.has(d.key))
            continue; // channel gone → drop (chart would too)
        const o = seriesOvr[d.key];
        if (o?.hidden)
            continue;
        const ownAxis = o?.axis ? o.axis === "own" : d.own_axis;
        out.push({
            key: d.key,
            own_axis: ownAxis,
            color: o?.color ?? d.color,
            visible_when: d.visible_when,
            added: false,
            customized: Boolean(o && (o.color != null || o.axis != null)),
        });
    }
    // 2. Added series: only existing channels, no dupes of declared or each other.
    const seen = new Set(out.map((s) => s.key));
    for (const a of override?.addedSeries ?? []) {
        if (declaredKeys.has(a.key))
            continue; // a declared key isn't an "add"
        if (seen.has(a.key))
            continue;
        if (!channelByKey.has(a.key))
            continue; // channel gone → drop
        seen.add(a.key);
        out.push({
            key: a.key,
            own_axis: a.axis === "own",
            color: a.color,
            added: true,
            customized: true,
        });
    }
    // 3. Resolve axes from the surviving series; attach axis overrides by id.
    const axisOrder = [];
    const axisMap = new Map();
    for (const s of out) {
        const unit = channelByKey.get(s.key)?.unit ?? null;
        const axisId = plotAxisId({ key: s.key, own_axis: s.own_axis, unit });
        let axis = axisMap.get(axisId);
        if (!axis) {
            const ao = axisOvr[axisId];
            axis = {
                axisId,
                label: s.own_axis
                    ? (channelByKey.get(s.key)?.label ?? s.key)
                    : (unit ?? ""),
                min: ao?.min ?? undefined,
                max: ao?.max ?? undefined,
                log: ao?.log ?? undefined,
                seriesKeys: [],
                customized: Boolean(ao && (ao.min != null || ao.max != null || ao.log != null)),
            };
            axisMap.set(axisId, axis);
            axisOrder.push(axisId);
        }
        axis.seriesKeys.push(s.key);
        // Denormalize axis settings onto the series (chart reads the first per group).
        s.yMin = axis.min ?? null;
        s.yMax = axis.max ?? null;
        s.log = axis.log;
    }
    const axes = axisOrder.map((id) => axisMap.get(id));
    const isCustomized = out.some((s) => s.added || s.customized) ||
        axes.some((a) => a.customized) ||
        Object.values(seriesOvr).some((o) => o.hidden);
    return { series: out, axes, isCustomized };
}
//# sourceMappingURL=plot-override.js.map